import styled from "styled-components";
import { RSRow, Separator, TableRow, TableSubtitleRow } from "./ExtendedThermalCalculation";
import { getFloorScreedTranslations, getBearingFloorTranslations } from "../translations";
import { useThermalState } from "./ThermalStateProvider";
import { useTranslation } from "react-i18next";
import { SyntheticEvent, useMemo, useState } from "react";
import { getEnumValue, parseMaterial, sanitizeNumericInput, toDisplay } from "../../../../services/helpers.service";
import { FloorBearingTypes, FloorInsulationTypes } from "@iko-design-center/shared/lib/constants/constants";
import { useExtendedThermalCalculation } from "./extendedCalculation.hook";

const TableBody = styled.div`
`;

export const PreliminaryTableBody = () => {
    const { t } = useTranslation() as any;
    const { state, setState, performance } = useThermalState();
    const { formatCalc } = useExtendedThermalCalculation();

    const [thicknessStr, setThicknessStr] = useState<Record<string, string>>(() => ({
        floorScreed: toDisplay(state.floorScreed?.material?.thickness),
        floorInsulation: toDisplay(state.floorInsulation?.material?.thickness),
        bearingFloor: toDisplay(state.bearingFloor?.material?.thickness),
    }));

    const bearingFloorOptions = useMemo(() => {
        return [
            { value: state.bearingFloor.material.id, label: getBearingFloorTranslations(t, state.bearingFloor.type) },
            { value: "REINFORCED_CONCRETE_FLOOR", label: getBearingFloorTranslations(t, FloorBearingTypes.REINFORCED_CONCRETE_FLOOR) },
            { value: "RIBBED_CASSETTE_FLOOR", label: getBearingFloorTranslations(t, FloorBearingTypes.RIBBED_CASSETTE_FLOOR) },
            { value: "HOLLOW_CORE_SLAB_FLOOR", label: getBearingFloorTranslations(t, FloorBearingTypes.HOLLOW_CORE_SLAB_FLOOR) },
            { value: "COMBINATION_FLOOR", label: getBearingFloorTranslations(t, FloorBearingTypes.COMBINATION_FLOOR) },
        ].filter((x, index, array) => index === array.findIndex((t) => t.value === x.value))
        // .sort((a, b) => a.label.localeCompare(b.label));
    }, [state.bearingFloor, t]);

    const handleEditableThicknessChange = (event: SyntheticEvent<HTMLInputElement>) => {
        const input = event.target as HTMLInputElement;
        const type = input.dataset.type!;
        const raw = sanitizeNumericInput(input.value);
        setThicknessStr(prev => ({ ...prev, [type]: raw }));

        const parsed = parseFloat(raw);
        const thickness = raw === "" || isNaN(parsed) ? undefined : parsed;
        const validThickness = thickness !== undefined && thickness > 0;

        if (type) {
            setState({
                ...state,
                [type]: {
                    ...state[type],
                    valid: validThickness,
                    material: { ...state[type].material, thickness },
                },
            });
        }
    }

    const handleEditableOptionChange = (event: SyntheticEvent<HTMLSelectElement>, EnumType: any) => {
        const input = event.target as HTMLInputElement
        const type = input.dataset.type!
        const material = parseMaterial(input.value)

        if (material) {
            setState({
                ...state,
                [type]: { ...state[type], type: getEnumValue(input.value, EnumType), material },
            });
            setThicknessStr(prev => ({ ...prev, [type]: toDisplay((material as any).thickness) }));
        }
    }

    return (
        <TableBody>
            <RSRow type={"RSI"} />

            <TableSubtitleRow>{t("extendedThermalCalculation.preliminary.subtitle")}</TableSubtitleRow>
            <Separator />

            {/* Floor screed */}
            <TableRow
                type="floorScreed"
                title={t("floor.overview.floorScreed").toUpperCase()}
                text={getFloorScreedTranslations(t, state.floorScreed.type)}
                thickness={performance.floorScreed.thickness!}
                lambdaValue={formatCalc(performance.floorScreed.lambda!)}
                insulationValue={formatCalc(performance.floorScreed.RValue!)}

                editableThickness={true}
                editableThicknessValue={thicknessStr.floorScreed}
                onEditableThicknessChange={handleEditableThicknessChange}
                editableThicknessValid={performance.floorScreed.valid}
            />

            {/* Floor insulation */}
            <TableRow
                type="floorInsulation"
                title={t("floor.overview.floorInsulation").toUpperCase()}
                text={
                    state.floorInsulation.type === FloorInsulationTypes.ENERTHERM_ALU
                        ? t("material.ENERTHERM_ALU")
                        : t("material.ENERTHERM_KR_ALU")
                }
                thickness={performance.floorInsulation.thickness!}
                lambdaValue={formatCalc(performance.floorInsulation.lambda!)}
                insulationValue={formatCalc(performance.floorInsulation.RValue!)}

                editableThickness={true}
                editableThicknessValue={thicknessStr.floorInsulation}
                onEditableThicknessChange={handleEditableThicknessChange}
                editableThicknessValid={performance.floorInsulation.valid}
            />

            {/* Bearing floor */}
            <Separator margin="0" />
            <TableRow
                type="bearingFloor"
                title={t("floor.overview.bearingFloor").toUpperCase()}
                text={getBearingFloorTranslations(t, state.bearingFloor.type)}
                thickness={performance.bearingFloor.thickness!}
                lambdaValue={formatCalc(performance.bearingFloor.lambda!)}
                insulationValue={formatCalc(performance.bearingFloor.RValue!)}

                editableThickness={true}
                editableThicknessValue={thicknessStr.bearingFloor}
                onEditableThicknessChange={handleEditableThicknessChange}
                editableThicknessValid={performance.bearingFloor.valid}

                options={bearingFloorOptions}
                onOptionChange={(event) => handleEditableOptionChange(event, FloorBearingTypes)}
            />

            <Separator margin="0" />

            <RSRow type={"RSE"} />
        </TableBody>
    )
}

